import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { faqs, business } from "../data/content";

export default function FAQ() {
  useSeo({
    title: `FAQ | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Answers to common questions about electrolysis, permanent hair removal, appointments, aftercare, and inclusive care at Soft Petal Electrolysis in ${business.city}, ${business.state}.`,
    path: "/faq",
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={5} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="FAQ"
            title="Everything you&rsquo;re"
            scriptWord="wondering"
            subtitle="If you don&rsquo;t find your answer here, we&rsquo;d love to hear from you."
          />
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <Accordion
            type="single"
            collapsible
            className="w-full bg-warm-white rounded-3xl border border-pale-rose/50 shadow-soft overflow-hidden divide-y divide-pale-rose/40"
            data-testid="faq-accordion"
          >
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-6 sm:px-8 border-none">
                <AccordionTrigger
                  className="text-left font-serif-display text-lg sm:text-xl py-6 hover:no-underline hover:text-petal [&[data-state=open]]:text-petal"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 font-body text-charcoal-soft leading-relaxed text-[0.98rem]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16 bg-blush/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Still curious?" scriptWord="Reach out." />
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="btn-ghost-petal" data-testid="faq-contact-btn">
              Send a Message
            </Link>
            <BookLink  className="btn-petal" data-testid="faq-book-btn">
              Book Consultation
              <ArrowRight size={14} />
            </BookLink>
          </div>
        </div>
      </section>
    </div>
  );
}
